import { z } from 'zod';

import { MAX_ITEM_BARCODES } from '../enums.js';

const factorDecimal = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/)
  .refine((v) => Number(v) > 0, { message: 'factor must be greater than zero' });
const priceDecimal = z.string().regex(/^-?\d+(\.\d{1,4})?$/);

/**
 * An additional selling unit for an item (the base unit is the item itself).
 * `factor` is how many base units this unit contains (e.g. 12 for a pack of 12).
 */
export const ItemUnitSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
  name: z.string().min(1).max(64),
  factor: z.string(),
  salePrice: priceDecimal.nullable(),
  purchasePrice: priceDecimal.nullable(),
  barcode: z.string().max(64).nullable(),
  /**
   * MORE codes that mean this same pack.
   *
   * One carton, many labels: the shop stocks the same product in ten colours,
   * each colour's carton carrying its own EAN, all of them 800 pieces at the
   * same price. With only `barcode` above the shop's only way to make all ten
   * scan was to create ten identical carton units — which is exactly what it
   * did, and the till's unit chooser then listed «كرتون 800×» ten times over.
   *
   * These are stored in `item_barcodes` with `item_unit_id` set, so a scan
   * resolves to THIS unit rather than to a single piece. Optional: an older
   * API omits it and clients match the primary alone, exactly as before.
   */
  barcodes: z.array(z.string().max(64)).optional(),
  sortOrder: z.number().int(),
  /**
   * false = HIDDEN: the unit stays on the product — its prices, barcodes and
   * customer-group prices kept — but is offered nowhere: not in the app or on
   * the website, not at the till, not to reps, not in a document's unit picker,
   * and its codes do not scan. The shop's "the stand is out, stop selling it
   * for now" switch. Past sales keep their own copy of the unit, untouched.
   */
  isActive: z.boolean(),
});
export type ItemUnit = z.infer<typeof ItemUnitSchema>;

export const ItemUnitInputSchema = z.object({
  name: z.string().min(1).max(64),
  factor: factorDecimal,
  salePrice: priceDecimal.optional(),
  purchasePrice: priceDecimal.optional(),
  barcode: z.string().max(64).optional(),
  /** Extra codes for THIS pack. Omit to leave this unit's extras untouched. */
  barcodes: z.array(z.string().max(64)).max(MAX_ITEM_BARCODES).optional(),
  /** false = keep the unit but hide it everywhere (see ItemUnit.isActive). Omitted = on sale. */
  isActive: z.boolean().optional(),
});
export type ItemUnitInput = z.infer<typeof ItemUnitInputSchema>;
